import { Search, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { MEAL_NAMES, MEALS } from '../../../shared/meals'
import { FormError } from '@/components/FormError'
import { KitchenStory } from '@/components/recipes/KitchenStory'
import { ChipButton, Ring } from '@/components/recipes/ModernRecipes'
import { RecipeWall } from '@/components/recipes/RecipeWall'
import { Avatar } from '@/components/tree/Avatar'
import { kitchens, narrow, type Chip, type Kitchen } from '@/lib/modernRecipes'
import { useAllRecipes, type Book } from '@/lib/recipes'

// A recipe book in the Modern style of Recipes: the kitchens its recipes come from as rings that play their cards,
// chips for its meals and kitchens with an ingredient box, then its recipes as whole dishes that turn over to their
// cards. The wall needs each recipe's tile and card, which the circle's list of recipes carries, so the book's are
// taken from there (the same recipes you may see, nothing more).
export function ModernBook({ book }: { book: Book }) {
  const recipes = useAllRecipes(book.familyId, false)
  const all = useMemo(
    () => (recipes.data?.recipes ?? []).filter((r) => r.book?.id === book.id),
    [recipes.data, book.id],
  )
  const kitchenList = useMemo(() => kitchens(all), [all])
  const [chip, setChip] = useState<Chip>(null)
  const [typed, setTyped] = useState('')
  const [playing, setPlaying] = useState<Kitchen | null>(null)
  const shown = useMemo(() => narrow(all, chip, typed), [all, chip, typed])
  const meals = MEALS.filter((m) => all.some((x) => x.meals.includes(m)))
  const same = (a: Chip, b: Chip) => JSON.stringify(a) === JSON.stringify(b)
  const toggle = (next: Chip) => setChip((now) => (same(now, next) ? null : next))

  return (
    <div className="flex flex-col gap-6 nav:gap-8">
      <FormError error={recipes.error} />
      {kitchenList.length > 0 && (
        <ul aria-label="Kitchens" className="-mx-4 scroll-row gap-3.5 px-4 pt-1 pb-1 nav:-mx-2 nav:px-2">
          {kitchenList.map((k) => (
            <li key={k.person.id} className="snap-start">
              <Ring label={k.person.name} onClick={() => setPlaying(k)} hint={`Play ${k.person.name}’s cards`}>
                <Avatar person={k.person} className="size-full text-xl shadow-none" />
              </Ring>
            </li>
          ))}
        </ul>
      )}
      {all.length > 1 && (
        <div
          role="group"
          aria-label="Narrow the recipes"
          className="-mx-4 scroll-row gap-2 px-4 nav:mx-0 nav:flex-wrap nav:px-0"
        >
          <label className="relative flex shrink-0 items-center">
            <Search className="pointer-events-none absolute left-3.5 size-4 text-muted-foreground" aria-hidden />
            <input
              type="search"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="Ingredient"
              aria-label="Find recipes that use an ingredient"
              className="h-10 w-40 rounded-full border bg-background pr-3 pl-9 text-sm outline-none placeholder:text-muted-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 nav:w-48"
            />
          </label>
          <ChipButton on={chip === null} onClick={() => setChip(null)}>
            All
          </ChipButton>
          {meals.map((meal) => (
            <ChipButton
              key={meal}
              on={same(chip, { kind: 'meal', meal })}
              onClick={() => toggle({ kind: 'meal', meal })}
            >
              {MEAL_NAMES[meal].one}
            </ChipButton>
          ))}
          {kitchenList.length > 1 &&
            kitchenList.map((k) => (
              <ChipButton
                key={k.person.id}
                on={same(chip, { kind: 'from', id: k.person.id })}
                onClick={() => toggle({ kind: 'from', id: k.person.id })}
              >
                <Avatar person={k.person} className="-ml-1.5 size-6 text-[0.625rem] shadow-none" />
                {k.person.name}
              </ChipButton>
            ))}
        </div>
      )}
      {all.length > 0 && shown.length === 0 && (
        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground">
          No recipes {typed.trim() ? `use “${typed.trim()}”` : 'here'}
          {chip ? ' with that tag' : ''}.
          <button
            type="button"
            onClick={() => {
              setChip(null)
              setTyped('')
            }}
            className="inline-flex min-h-11 items-center gap-1 font-medium text-foreground underline-offset-4 hover:underline"
          >
            <X className="size-4" aria-hidden /> Clear
          </button>
        </p>
      )}
      {shown.length > 0 && <RecipeWall recipes={shown} typed={typed} />}
      {playing && <KitchenStory kitchen={playing} onClose={() => setPlaying(null)} />}
    </div>
  )
}
